import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { debounce, isNil } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedRestaurantId } from '../store/auth/selectors';
import './Session.scss';
import { useGetMeQuery, useLazyGetActiveSessionsQuery, useUpsertGuestMutation } from '../store/users/usersApi';
import { useUserSse } from './sse/userSse.hook';
import { selectCsrfToken } from '../store/shared/selectors/auth.selectors';
import { setAuthState, setUserOrderSessionId } from '../store/auth/authSlice';

export const Session = (): React.JSX.Element => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const selectedRestaurantId = useSelector(selectSelectedRestaurantId);
	const { data: user, isLoading: isLoadingMe } = useGetMeQuery();
	const [getSessions] = useLazyGetActiveSessionsQuery();
	const [upsertGuestUser] = useUpsertGuestMutation();
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isSessionDataLoaded, setIsSessionDataLoaded] = useState(false);
	const csrfToken = useSelector(selectCsrfToken);

	useUserSse();

	const debouncedUpsertGuest = useMemo(
		() =>
			debounce(
				async () => {
					await upsertGuestUser().unwrap();
				},
				1000,
				{ leading: true, trailing: false }
			), // fire immediately, ignore duplicates for 1s
		[upsertGuestUser]
	);

	useEffect(() => {
		if (!isLoadingMe) {
			if ((isNil(user) && !isNil(selectedRestaurantId)) || (!isNil(user) && user?.customData?.principalKind === 'GUEST' && isNil(csrfToken))) {
				debouncedUpsertGuest().then(() => {
					setIsAuthenticated(true);
				});
			} else if (!isNil(user) && ((user?.principalKind?.principalKind === 'GUEST' && !isNil(csrfToken)) || user?.customData?.principalKind !== 'GUEST')) {
				setIsAuthenticated(true);
			} else {
				setIsAuthenticated(false);
			}
		}
	}, [csrfToken, debouncedUpsertGuest, isLoadingMe, selectedRestaurantId, user]);

	const getSessionsData = useMemo(
		() =>
			debounce(
				async () => {
					// Fetch users active session
					const { tableSessionId, userOrderSessionId } = await getSessions().unwrap();

					setIsSessionDataLoaded(true);
					dispatch(setAuthState({ tableSessionId, restaurantId: selectedRestaurantId }));
					dispatch(setUserOrderSessionId({ userOrderSessionId }));
				},
				500,
				{ leading: true, trailing: false }
			), // fire immediately, ignore duplicates for 0.5s
		[dispatch, getSessions, selectedRestaurantId]
	);

	useEffect(() => {
		if (isNil(selectedRestaurantId)) {
			navigate('/');
		} else if (isAuthenticated) {
			getSessionsData();
		}
	}, [csrfToken, dispatch, getSessionsData, isAuthenticated, navigate, selectedRestaurantId, user]);

	return (
		<>
			{isAuthenticated && isSessionDataLoaded ? (
				<div className="tableSessionContainer">{!isNil(selectedRestaurantId) ? <Outlet /> : <div>loading</div>}</div>
			) : (
				<div>Loading auth</div>
			)}
		</>
	);
};
